"use client";

import React, { useState, useCallback, useRef, useEffect } from 'react';
import SecurityHealthScore from './SecurityHealthScore';
import KPIBentoGrid from './KPIBentoGrid';
import ThreatTimeline from './ThreatTimeline';
import LiveThreatFeed from './LiveThreatFeed';
import BDCompliancePanel from './BDCompliancePanel';
import AttackCategoryChart from './AttackCategoryChart';
import { useLanguage } from '@/lib/language-context';
import { t } from '@/lib/translations';
import { Download, Clock, ShieldAlert, X, Zap, ChevronDown, ChevronUp } from 'lucide-react';
import HeroBanner from '@/components/HeroBanner';
import { useThreatFeed, ThreatFeedItem } from '@/lib/supabase/realtime-hooks';
import { useTapRipple } from '@/hooks/useMobileInteractions';

// ─── Collapsible Panel ────────────────────────────────────────────────────────

interface CollapsiblePanelProps {
  title: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
  className?: string;
}

function CollapsiblePanel({ title, defaultOpen = true, children, className = '' }: CollapsiblePanelProps) {
  const [open, setOpen] = useState(defaultOpen);
  const { triggerRipple } = useTapRipple();

  return (
    <div className={`${className}`}>
      <button
        onClick={() => setOpen(!open)}
        onPointerDown={triggerRipple}
        className="ripple-container press-scale w-full flex items-center justify-between py-2 px-1 text-left md:hidden mb-2 rounded-lg"
        aria-expanded={open}
        aria-controls={`panel-${title.replace(/\s+/g, '-').toLowerCase()}`}
      >
        <span className="text-xs font-semibold text-cyber-muted uppercase tracking-widest font-mono">{title}</span>
        {open
          ? <ChevronUp size={14} className="text-cyber-muted" />
          : <ChevronDown size={14} className="text-cyber-muted" />
        }
      </button>
      <div
        id={`panel-${title.replace(/\s+/g, '-').toLowerCase()}`}
        className={`transition-all duration-300 overflow-hidden ${open ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0 md:max-h-[2000px] md:opacity-100'}`}
      >
        {children}
      </div>
    </div>
  );
}

// ─── Live Alert Banner ────────────────────────────────────────────────────────

interface LiveAlertBannerProps {
  threat: ThreatFeedItem;
  onDismiss: () => void;
}

function LiveAlertBanner({ threat, onDismiss }: LiveAlertBannerProps) {
  const [visible, setVisible] = useState(false);
  const { triggerRipple } = useTapRipple();

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 20);
    return () => clearTimeout(t);
  }, []);

  const handleDismiss = () => {
    setVisible(false);
    setTimeout(onDismiss, 300);
  };

  const isCritical = threat.severity === 'critical';
  const isHigh = threat.severity === 'high';

  const borderColor = isCritical
    ? 'border-red-500/70'
    : isHigh
    ? 'border-orange-500/60' :'border-yellow-500/50';
  const bgColor = isCritical
    ? 'bg-red-950/60'
    : isHigh
    ? 'bg-orange-950/50' :'bg-yellow-950/40';
  const glowColor = isCritical
    ? 'shadow-[0_0_20px_rgba(255,59,59,0.25)]'
    : isHigh
    ? 'shadow-[0_0_16px_rgba(255,107,53,0.20)]'
    : '';
  const badgeColor = isCritical
    ? 'bg-red-500 text-white'
    : isHigh
    ? 'bg-orange-500 text-white' :'bg-yellow-500 text-black';
  const iconColor = isCritical
    ? 'text-red-400'
    : isHigh
    ? 'text-orange-400' :'text-yellow-400';
  const stripeColor = isCritical ? 'bg-red-500' : isHigh ? 'bg-orange-500' : 'bg-yellow-500';

  return (
    <div
      className={`
        ripple-container relative overflow-hidden rounded-xl border backdrop-blur-md px-4 py-3
        flex items-center gap-3
        transition-all duration-300 ease-out press-scale
        ${borderColor} ${bgColor} ${glowColor}
        ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2'}
      `}
      onPointerDown={triggerRipple}
      role="alert"
      aria-live="assertive"
      aria-label={`New ${threat.severity} threat detected: ${threat.threat_type}`}
    >
      {/* Left stripe */}
      <div className={`absolute left-0 top-0 bottom-0 w-1 ${stripeColor}`} />

      {/* Pulse dot */}
      <div className="shrink-0 flex items-center gap-1.5 pl-1">
        <div className={`w-2 h-2 rounded-full ${stripeColor} live-dot`} />
        <ShieldAlert size={15} className={iconColor} />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0 flex items-center gap-3 flex-wrap">
        <span className={`text-[9px] font-mono font-bold px-1.5 py-0.5 rounded shrink-0 ${badgeColor}`}>
          {threat.severity.toUpperCase()}
        </span>
        <div className="flex items-center gap-1.5 min-w-0">
          <Zap size={11} className={iconColor} />
          <span className="text-[12px] font-semibold text-white truncate">
            New Threat Detected:
          </span>
          <span className="text-[12px] text-cyber-text font-mono truncate">{threat.threat_type}</span>
        </div>
        <div className="flex items-center gap-3 text-[11px] font-mono text-cyber-muted flex-wrap">
          <span>
            Target: <span className="text-cyber-text">{threat.target}</span>
          </span>
          <span className="hidden sm:inline">·</span>
          <span className="hidden sm:inline">
            Source: <span className="text-cyber-text">{threat.source_ip}</span>
          </span>
          <span className="hidden md:inline">·</span>
          <span className="hidden md:inline">
            Technique: <span className="text-cyber-purple">{threat.technique}</span>
          </span>
          <span className="hidden lg:inline">·</span>
          <span className="hidden lg:inline">
            ID: <span className="text-cyber-blue">{threat.threat_id}</span>
          </span>
        </div>
      </div>

      {/* Dismiss */}
      <button
        onClick={handleDismiss}
        onPointerDown={(e) => { e.stopPropagation(); triggerRipple(e); }}
        className="ripple-container press-scale-sm shrink-0 w-6 h-6 flex items-center justify-center rounded-md hover:bg-white/10 transition-colors text-cyber-muted hover:text-cyber-text"
        aria-label="Dismiss threat alert"
      >
        <X size={13} />
      </button>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function OverviewDashboardContent() {
  const { language } = useLanguage();
  const [timeRange, setTimeRange] = useState<'24h' | '7d' | '30d'>('24h');
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [liveAlerts, setLiveAlerts] = useState<ThreatFeedItem[]>([]);
  const initialLoadRef = useRef(true);
  const seenIdsRef = useRef<Set<string>>(new Set());
  const { triggerRipple } = useTapRipple();

  const handleNewThreat = useCallback((item: ThreatFeedItem) => {
    if (initialLoadRef.current) return;
    if (seenIdsRef.current.has(item.id)) return;
    seenIdsRef.current.add(item.id);

    setLastRefresh(new Date());
    setLiveAlerts((prev) => [item, ...prev].slice(0, 3));

    if (item.severity !== 'critical') {
      setTimeout(() => {
        setLiveAlerts((prev) => prev.filter((a) => a.id !== item.id));
      }, 10000);
    }
  }, []);

  const { loading } = useThreatFeed(handleNewThreat);

  useEffect(() => {
    if (!loading && initialLoadRef.current) {
      const t = setTimeout(() => {
        initialLoadRef.current = false;
      }, 500);
      return () => clearTimeout(t);
    }
  }, [loading]);

  const dismissAlert = useCallback((id: string) => {
    setLiveAlerts((prev) => prev.filter((a) => a.id !== id));
  }, []);

  const timeOptions: { key: '24h' | '7d' | '30d'; label: string }[] = [
    { key: '24h', label: t(language, 'last_24h') },
    { key: '7d', label: t(language, 'last_7d') },
    { key: '30d', label: t(language, 'last_30d') },
  ];

  return (
    <div className="space-y-4 md:space-y-6 swipe-in-right">
      {/* ── AI Corporate Hero Banner ── */}
      <HeroBanner variant="full" />

      {/* ── Live Threat Alert Banners ── */}
      {liveAlerts.length > 0 && (
        <div className="space-y-2" aria-label="Live threat alerts">
          {liveAlerts.map((alert) => (
            <LiveAlertBanner
              key={alert.id}
              threat={alert}
              onDismiss={() => dismissAlert(alert.id)}
            />
          ))}
        </div>
      )}

      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className={`text-xl md:text-2xl font-bold text-white ${language === 'bn' ? 'font-bangla' : ''}`}>
            {t(language, 'dashboard_title')}
          </h1>
          <p className={`text-sm text-cyber-muted mt-0.5 ${language === 'bn' ? 'font-bangla' : ''}`}>
            {t(language, 'dashboard_subtitle')}
          </p>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {/* Time Range */}
          <div className="flex items-center gap-1 bg-[#0F1117] border border-cyber-border rounded-lg p-1">
            {timeOptions.map((opt) => (
              <button
                key={opt.key}
                onClick={() => setTimeRange(opt.key)}
                onPointerDown={triggerRipple}
                className={`ripple-container press-scale px-2.5 py-1.5 rounded-md text-xs font-medium transition-all duration-150 ${
                  timeRange === opt.key
                    ? 'bg-cyber-blue/20 text-cyber-blue border border-cyber-blue/30' :'text-cyber-muted hover:text-cyber-text'
                } ${language === 'bn' ? 'font-bangla' : ''}`}
              >
                {opt.label}
              </button>
            ))}
          </div>

          {/* Last Updated — hidden on small screens */}
          <div className="hidden sm:flex items-center gap-1.5 text-[11px] text-cyber-muted font-mono">
            <Clock size={11} />
            <span>
              {t(language, 'last_updated')}:{' '}
              {lastRefresh.toLocaleTimeString('en-BD', {
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
              })}
            </span>
          </div>

          <button
            onPointerDown={triggerRipple}
            className="ripple-container press-scale flex items-center gap-2 px-3 py-1.5 bg-white/5 border border-cyber-border rounded-lg text-xs text-cyber-muted hover:bg-cyber-blue/10 hover:border-cyber-blue/30 hover:text-cyber-blue transition-all duration-150"
          >
            <Download size={13} />
            <span className={language === 'bn' ? 'font-bangla' : ''}>{t(language, 'export')}</span>
          </button>
        </div>
      </div>

      {/* Top Section: Health Score + KPI Cards — stacked on mobile/tablet */}
      <CollapsiblePanel title="Security Overview">
        <div className="grid grid-cols-1 xl:grid-cols-4 2xl:grid-cols-5 gap-4">
          <div className="xl:col-span-1 2xl:col-span-1">
            <SecurityHealthScore />
          </div>
          <div className="xl:col-span-3 2xl:col-span-4">
            <KPIBentoGrid />
          </div>
        </div>
      </CollapsiblePanel>

      {/* Charts Row — stacked on mobile, side-by-side on xl */}
      <CollapsiblePanel title="Threat Analytics">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          <div className="md:col-span-2 xl:col-span-2">
            <ThreatTimeline timeRange={timeRange} />
          </div>
          <div className="md:col-span-2 xl:col-span-1">
            <AttackCategoryChart />
          </div>
        </div>
      </CollapsiblePanel>

      {/* Bottom Row: Live Feed + Compliance — stacked on mobile, side-by-side on xl */}
      <CollapsiblePanel title="Live Feed & Compliance">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          <div className="md:col-span-2 xl:col-span-2">
            <LiveThreatFeed />
          </div>
          <div className="md:col-span-2 xl:col-span-1">
            <BDCompliancePanel />
          </div>
        </div>
      </CollapsiblePanel>
    </div>
  );
}